import React from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const STAT_FIELDS = [
  { key: 'points', label: 'PTS', max: 100 },
  { key: 'rebounds', label: 'REB', max: 30 },
  { key: 'assists', label: 'AST', max: 30 },
  { key: 'steals', label: 'STL', max: 15 },
  { key: 'blocks', label: 'BLK', max: 15 },
];

const TeamBadge = ({ team }) => {
  if (!team) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full font-caption text-xs bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
      —
    </span>
  );
  const isWinner = team === 'Winners';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-caption text-xs font-semibold ${
      isWinner ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400'
    }`}>
      <Icon name={isWinner ? 'Trophy' : 'ThumbsDown'} size={10} color="currentColor" />
      {isWinner ? 'Won' : 'Lost'}
    </span>
  );
};

const StatsTable = ({ selectedPlayers, stats, onStatChange, errors, playerTeams, showDetailedStats = false }) => {
  const visibleFields = showDetailedStats ? STAT_FIELDS : STAT_FIELDS.filter((f) => f.key === 'points');
  if (selectedPlayers?.length === 0) {
    return (
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] p-8 text-center">
        <Icon name="Users" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
        <p className="font-heading font-semibold text-base" style={{ color: 'var(--color-muted-foreground)' }}>
          No players selected
        </p>
        <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
          Select players above to enter their statistics
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] overflow-hidden">
      <div className="p-4 md:p-5 border-b border-[var(--color-border)]">
        <h3 className="font-heading font-semibold text-base md:text-lg" style={{ color: 'var(--color-foreground)' }}>
          Player Statistics
        </h3>
        <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
          Enter stats for each selected player
        </p>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--color-border)]" style={{ backgroundColor: 'var(--color-muted)' }}>
              <th className="text-left px-4 py-3 font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-muted-foreground)', minWidth: '180px' }}>
                Player
              </th>
              <th className="text-center px-3 py-3 font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-muted-foreground)', minWidth: '70px' }}>
                Result
              </th>
              {visibleFields?.map((f) => (
                <th key={f?.key} className="text-center px-3 py-3 font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-muted-foreground)', minWidth: '80px' }}>
                  {f?.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {selectedPlayers?.map((player, idx) => (
              <tr
                key={player?.id}
                className={`border-b border-[var(--color-border)] last:border-0 ${idx % 2 === 0 ? '' : 'bg-[var(--color-muted)]/30'}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                      <Image src={player?.avatar} alt={player?.avatarAlt} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{player?.name}</p>
                      <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>#{player?.number} · {player?.position}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-center">
                  <TeamBadge team={playerTeams?.[player?.id]} />
                </td>
                {visibleFields?.map((f) => (
                  <td key={f?.key} className="px-3 py-3 text-center">
                    <div className="flex flex-col items-center">
                      <input
                        type="number"
                        min="0"
                        max={f?.max}
                        value={stats?.[player?.id]?.[f?.key] ?? ''}
                        onChange={(e) => onStatChange(player?.id, f?.key, e?.target?.value)}
                        placeholder="0"
                        className={`w-16 text-center py-1.5 px-2 rounded-lg border font-data text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${
                          errors?.[player?.id]?.[f?.key]
                            ? 'border-red-400 bg-red-50 dark:bg-red-950/20' :'border-[var(--color-input)] bg-[var(--color-muted)]'
                        }`}
                        style={{ color: 'var(--color-foreground)' }}
                        aria-label={`${player?.name} ${f?.label}`}
                      />
                      {errors?.[player?.id]?.[f?.key] && (
                        <span className="text-xs text-red-500 mt-0.5">{errors?.[player?.id]?.[f?.key]}</span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-[var(--color-border)]">
        {selectedPlayers?.map((player) => (
          <div key={player?.id} className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                <Image src={player?.avatar} alt={player?.avatarAlt} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{player?.name}</p>
                <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>#{player?.number} · {player?.position}</p>
              </div>
              <TeamBadge team={playerTeams?.[player?.id]} />
            </div>
            <div className={`grid gap-2 ${showDetailedStats ? 'grid-cols-5' : 'grid-cols-1 max-w-[120px]'}`}>
              {visibleFields?.map((f) => (
                <div key={f?.key} className="flex flex-col items-center gap-1">
                  <label className="font-caption text-xs font-semibold uppercase" style={{ color: 'var(--color-muted-foreground)' }}>
                    {f?.label}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={f?.max}
                    value={stats?.[player?.id]?.[f?.key] ?? ''}
                    onChange={(e) => onStatChange(player?.id, f?.key, e?.target?.value)}
                    placeholder="0"
                    className={`w-full text-center py-2 px-1 rounded-lg border font-data text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${
                      errors?.[player?.id]?.[f?.key]
                        ? 'border-red-400 bg-red-50' :'border-[var(--color-input)] bg-[var(--color-muted)]'
                    }`}
                    style={{ color: 'var(--color-foreground)' }}
                    aria-label={`${player?.name} ${f?.label}`}
                  />
                  {errors?.[player?.id]?.[f?.key] && (
                    <span className="text-xs text-red-500 text-center leading-tight">{errors?.[player?.id]?.[f?.key]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsTable;